import { Button, styled, useTheme } from '@mui/material';
import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { Typography } from './Typography/Typography';
import { ReactComponent as DownloadIcon } from '../assets/icons/download-blue-small.svg';
import { CSVLink } from 'react-csv';
import {
  KPISValueResponse,
  QUESTION_TYPE,
  QuestionOption,
  QuestionRow,
  QuestionTableData,
} from '../types';
import { KPI_VALUE_COLUMN_ID } from '../constants/defaultValues';

const Wrapper = styled('div')`
  position: relative;
`;

interface Props {
  data: QuestionRow[];
  surveyName: string;
  showOverrideColumn?: boolean;
  label?: string;
}

export const ExportButton: FC<Props> = ({ data, surveyName, showOverrideColumn, label }) => {
  const { colors } = useTheme();

  const csvQuestionsData = useMemo(() => {
    const filteredData = data.filter((row) => row.type !== QUESTION_TYPE.TABLE);
    let result = [['Question', 'Answer']];

    if (showOverrideColumn) {
      result = [['Question', 'Answer', 'Override']];
    }

    filteredData.forEach((row) => {
      if (row.type === QUESTION_TYPE.SINGLE) {
        const csvCommonRow = [row.question, (row.answer as QuestionOption)?.name ?? ''];
        const csvRow = showOverrideColumn
          ? [...csvCommonRow, (row.overrideAnswer as QuestionOption)?.name ?? '']
          : csvCommonRow;
        result.push(csvRow);
      } else if (row.type === QUESTION_TYPE.MULTI) {
        result.push([
          row.question,
          (row.answer as QuestionOption[])?.map((option) => option.name).join(', ') ?? '',
        ]);
      } else if (row.type === QUESTION_TYPE.YES_NO) {
        result.push([row.question, (row.answer as QuestionOption)?.name ?? '']);
      } else {
        result.push([row.question, (row.answer as string) ?? '']);
      }
    });
    return result;
  }, [data, showOverrideColumn]);

  const getFileName = (name: string) => {
    return name
      .split(' ')
      .join('-')
      .replace(/[.,/#!$%^&*;:{}=_`~()]/g, '')
      .toLowerCase()
      .trim();
  };

  const fileName = useMemo(() => getFileName(surveyName), [surveyName]);

  const tableTypeQuestionsData = useMemo(() => {
    const filteredData = data.filter((row) => row.type === QUESTION_TYPE.TABLE);
    const result: { fileName: string; data: string[][]; id: string }[] = [];
    filteredData.forEach((row) => {
      const { columns, rows } = row?.answer as QuestionTableData || {};
      const csvData: string[][] = [];
      rows?.forEach((row) => {
        const csvRow: string[] = [];
        columns.forEach((column) => {
          if (column === KPI_VALUE_COLUMN_ID) {
            csvRow.push((row[column] as KPISValueResponse)?.value ?? '');
            return;
          }
          csvRow.push((row[column] as string) ?? '');
        });
        csvData.push(csvRow);
      });
      const item = {
        id: row.id,
        fileName: getFileName(row.question),
        data: csvData,
      };
      result.push(item);
    });
    return result;
  }, [data]);

  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      const button = document.getElementById(`${fileName}-questions`);
      if (button) {
        button.click();
      }
      tableTypeQuestionsData.forEach((item) => {
        const button = document.getElementById(`${item.id}-${item.fileName}`);
        if (button) {
          button.click();
        }
      });
    },
    [fileName, tableTypeQuestionsData]
  );

  return (
    <Wrapper>
      <Button
        variant='text'
        startIcon={<DownloadIcon />}
        style={{ minWidth: 'auto' }}
        onClick={onClick}
      >
        <Typography variant='subtitle2' color={colors.accent[50]}>
          {label || 'Export as CSV'}
        </Typography>
      </Button>
      <CSVLink data={csvQuestionsData} filename={fileName}>
        <Button
          variant='text'
          id={`${fileName}-questions`}
          style={{ visibility: 'hidden', width: '0', position: 'absolute' }}
        />
      </CSVLink>
      {tableTypeQuestionsData.map((item, i) => (
        <CSVLink key={i} data={item.data} filename={`${item.fileName}.csv`}>
          <Button
            variant='text'
            id={`${item.id}-${item.fileName}`}
            style={{ visibility: 'hidden', width: '0', position: 'absolute' }}
          />
        </CSVLink>
      ))}
    </Wrapper>
  );
};
